<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRefs } from 'vue';

import usersRepository from '@/api/usersRepository';
import { useUserStore } from '@/stores/user';

interface Props {
  initials?: string;
}

defineProps<Props>();

const userStore = useUserStore();
const { currentUser } = toRefs(userStore);

const ACCOUNTS_BASE_URL = import.meta.env.VITE_ACCOUNTS_BASE_URL;

const isActive = ref(false);
const avatarRef = ref<HTMLDivElement | null>(null);

const adminUrl = computed(() => `${ACCOUNTS_BASE_URL}admin`);

const closeDropdown = (event: Event) => {
  const { target } = event;

  if (avatarRef.value !== target && !avatarRef.value?.contains(target as Node)) {
    isActive.value = false;
  }
};

const toggleActive = () => {
  isActive.value = !isActive.value;
};

const logout = async () => {
  await usersRepository.logout();
};

onMounted(() => {
  window.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  window.removeEventListener('click', closeDropdown);
});
</script>

<template>
  <div
    ref="avatarRef"
    class="user-dropdown"
  >
    <button
      v-if="initials"
      :class="{ 'user-dropdown__btn--active': isActive }"
      class="user-dropdown__btn"
      @click="toggleActive"
    >
      <span>
        {{ initials }}
      </span>
    </button>
    <transition name="fade">
      <ul
        v-if="isActive"
        class="user-dropdown__list"
      >
        <li>
          <a
            href="https://influence-help.brandwatch.com/en/"
            target="_blank"
            @click="isActive = false"
          >
            {{ $t('header.helpCenter') }}
          </a>
        </li>
        <li>
          <a
            :href="ACCOUNTS_BASE_URL"
            target="_blank"
            @click="isActive = false"
          >
            {{ $t('header.myAccount') }}
          </a>
        </li>
        <li v-if="currentUser?.superAdmin">
          <a
            :href="adminUrl"
            target="_blank"
            @click="isActive = false"
          >
            {{ $t('header.admin') }}
          </a>
        </li>
        <li>
          <p @click="logout">
            {{ $t('header.logOut') }}
          </p>
        </li>
      </ul>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.user-dropdown {
  height: 60px;
  border-right: none !important;
  position: relative;
  z-index: 1;

  &__btn {
    height: 100%;
    padding: 0 20px;
    border: none;
    background-color: transparent;
    transition: background-color 300ms;
    cursor: pointer;

    &--active {
      background-color: var(--gray);
    }

    &:hover:not(.user-dropdown__btn--active) {
      background-color: var(--gray);
    }
  }

  &__list {
    right: 5px;
    box-shadow: 0 5px 10px 0 rgb(12 30 48 / 10%);
    top: calc(100% + 4px);
    position: absolute;
    list-style: none;
    border: 1px solid var(--gray-dark);
  }

  li {
    min-width: 170px;

    &:first-child {
      border-top-left-radius: 3px;
      border-top-right-radius: 3px;
      border-top: 0;
    }

    &:last-child {
      border-bottom-left-radius: 3px;
      border-bottom-right-radius: 3px;
    }

    &:hover {
      color: var(--blue);
    }

    p,
    a,
    a:visited {
      display: block;
      padding: 15px;
      color: var(--text-color);
      text-decoration: none;
      margin-bottom: 0;
      transition: 300ms;

      &:hover {
        color: var(--blue);
      }
    }

    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid var(--gray);
    background-color: var(--white);
    line-height: normal;
    transition-duration: 300ms;
    cursor: pointer;
  }

  span {
    display: block;
    background-color: #f6f8fb;
    border: 1px solid #cbd3dc;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 500;
    width: 24px;
    height: 24px;
    line-height: 2;
    color: var(--text-color-gray);
  }
}
</style>
